/*eslint no-unused-vars: "off"*/
import {useCallback, useReducer, useState} from "react";
import {getDistance} from "geolib";
import useDebounce from "../../utils/hooks/useDebounce";
import useInputs from "../../utils/hooks/useInputs";
import {
  INITIAL_VIEW_STATE as CONSTANTS_INITIAL_VIEW_STATE,
  INITIAL_GEOJSON_POLYGON_LAYER_PROPS as CONSTANTS_INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
  INITIAL_GEOJSON_POINT_LAYER_PROPS as CONSTANTS_INITIAL_GEOJSON_POINT_LAYER_PROPS,
  INITIAL_POINT_LAYER_PROPS as CONSTANTS_INITIAL_POINT_LAYER_PROPS, INITIAL_MAP_TYPE,
  INITIAL_LAYER_WMS_TYPES as CONSTANTS_INITIAL_LAYER_WMS_TYPES,
} from "../../Constants";
import {shuffle} from "../../utils";
import useIconDict from "../../utils/useIcons";

import {
  reducer as reducerViewState,
  ACTIONS as ACTIONS_VIEWSTATE,
} from "./reducerViewState";


const useMapProps = ({ INITIAL_VISIBLE_RADIUS,
                       INITIAL_VIEW_STATE,
                       INITIAL_GEOJSON_EXTRA_PARAMS,
                       INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
                       INITIAL_GEOJSON_POINT_LAYER_PROPS,
                       INITIAL_POINT_LAYER_PROPS,
                       INITIAL_LAYER_WMS_TYPES,
                       INITIAL_WIDGET_STATE }) => {
  const [viewState, dispatchViewState] = useReducer(reducerViewState, {
    ...CONSTANTS_INITIAL_VIEW_STATE,
    ...INITIAL_VIEW_STATE,
  });
  const {latitude, longitude} = viewState;

  const [widgetState, onChangeWidgetState] = useInputs(INITIAL_WIDGET_STATE);

  const [visibleRadius, setVisibleRadius] = useState(INITIAL_VISIBLE_RADIUS);

  const [mapBounds, setMapBounds] = useState();

  const [mapDistrictStats, setMapDistrictStats] = useState([
    {},  // metric object
    0,   // metric max
  ]);
  const isChangedMapDistrictStats = useCallback((newMapDistrictStats) => {
    return (JSON.stringify(mapDistrictStats) !== JSON.stringify(newMapDistrictStats));
  }, [mapDistrictStats]);

  const [geojsonExtraParams, setGeojsonExtraParams] = useState({
    ...INITIAL_GEOJSON_EXTRA_PARAMS,
  });

  const [geojsonPolygonLayerProps, setGeojsonPolygonLayerProps] = useState({
    ...CONSTANTS_INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
    ...INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
  });
  useDebounce(() => {
    const {
        getFillColor, getLevelFillColor,
        getLineColor, getLevelLineColor,
        getHoverHtml,
    } = {
      ...CONSTANTS_INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
      ...INITIAL_GEOJSON_POLYGON_LAYER_PROPS,
    };

    let updateProps = {
      getFillColor: feature => {
        const newFeature = correctFeatureProperties(feature);
        if ( getLevelFillColor ) {
          return getLevelFillColor(newFeature, mapDistrictStats);
        }
        if ( getFillColor ) {
          return getFillColor(newFeature);
        }
        return undefined;
      },
      getLineColor: feature => {
        const newFeature = correctFeatureProperties(feature);
        if ( getLevelLineColor ) {
          return getLevelLineColor(newFeature, mapDistrictStats);
        }
        if ( getLineColor ) {
          return getLineColor(newFeature);
        }
        return undefined;
      },
    };

    if ( getHoverHtml ) {
      updateProps = {
        ...updateProps,
        getHoverHtml: feature => {
          const newFeature = correctFeatureProperties(feature);
          if ( getHoverHtml )
            return getHoverHtml(newFeature, mapDistrictStats);
          return undefined;
        },
      }
    }

    setGeojsonPolygonLayerProps(props => ({
      ...props,
      ...updateProps,
    }));

    // geojson Polygon Layers들을 강제로 업데이트하기 위해, shuffle을 수행했습니다.
    setGeojsonPolygonsResponseList(layers => shuffle(layers));
  }, 200, [latitude, longitude, mapDistrictStats]);

  const [geojsonPointLayerProps, setGeojsonPointLayerProps] = useState({
    ...CONSTANTS_INITIAL_GEOJSON_POINT_LAYER_PROPS,
    ...INITIAL_GEOJSON_POINT_LAYER_PROPS,
  });

  const [pointsLayerFilterFunc, setPointsLayerFilterFunc] = useState(() => () => true);
  const [pointLayerProps, setPointLayerProps] = useState({
    ...CONSTANTS_INITIAL_POINT_LAYER_PROPS,
    ...INITIAL_POINT_LAYER_PROPS,
  });

  const setMapCenter = useCallback(({ latitude, longitude, zoom }) => {
    dispatchViewState({
      type: ACTIONS_VIEWSTATE.UPDATE_VIEWPORT,
      payload: {
        latitude,
        longitude,
        zoom,
      }
    });
  }, []);

  const setZoom = useCallback(({ zoom }) => {
    dispatchViewState({
      type: ACTIONS_VIEWSTATE.UPDATE_ZOOM,
      payload: {
        zoom,
      }
    });
  }, []);

  const [currentDistrictCode, setCurrentDistrictCode] = useState();
  const [currentDistrictName, setCurrentDistrictName] = useState();
  const [manualDistrictLayerParamsByGeoserver, setManualDistrictLayerParamsByGeoserver] = useState({
    typeName: null,    // ex) KCA:TL_SCCO_SID_SIMPLE, KCA:TL_SCCO_SIG_SIMPLE
    CQL_FILTER: null,  // ex) "SIG_CD LIKE '11%'"
  });

  const [geojsonPolygonsResponseList, setGeojsonPolygonsResponseList] = useState([]);
  // const [polygonLabelPromiseList, setPolygonLabelPromiseList] = useState([]);
  const [pointsResponseList, setPointsResponseList] = useState([]);
  const [filteredPointsResponseList, setFilteredPointsResponseList] = useState([]);


  useDebounce(() => {
    const center = {latitude, longitude};

    setFilteredPointsResponseList(prevList => {
      const newList = pointsResponseList.map(pointsResponse => {
        const filtered = pointsResponse.filter(pointsLayerFilterFunc);

        //거리 제한 없이 마커 표시할 경우. 2025-01-09 MHS
        //return filtered;
        
        if (!visibleRadius) {
          return filtered;
        }
        return filtered.filter(({position: [longitude, latitude]}) => {
          const distance = getDistance(center, {latitude, longitude});
          return distance <= visibleRadius;
        });
        
      });

      if ( JSON.stringify(prevList) === JSON.stringify(newList) ) {
        return prevList;
      }
      return newList;

    });
  }, 400,[latitude, longitude, visibleRadius, pointsResponseList, pointsLayerFilterFunc]);


  const [dataResponseList, setDataResponseList] = useState([]);

  const [isEnableDistanceToClickedPoint, setIsEnableDistanceToClickedPoint] = useState(false);
  const [clickedPointIconObjects, setClickedPointIconObjects] = useState([]);
  const [clickedPolygonGeojsonObjects, setClickedPolygonGeojsonObjects] = useState([]);

  const [isHideFilteredPointsLayer, setIsHideFilteredPointsLayer] = useState(false);

  const [tooltipParams, setTooltipParams] = useState({});

  const [mapViewType, setMapViewType] = useState();  // undefined: 주소 검색, 허가번호 검색 : SearchByUid
  const setNullForMapViewType = useCallback(() => {
    setMapViewType(null);
  }, []);
  const setSearchByUidForMapViewType = useCallback(() => {
    setMapViewType('SearchByUid');
  }, []);

  const [mapType, setMapType] = useState(INITIAL_MAP_TYPE);

  const [mapLayerNames, setMapLayerNames] = useState([]);
  const updateMapLayerNames = useCallback((layerName, isUse) => {
    setMapLayerNames(names => {
      if ( isUse && names.indexOf(layerName) === -1 )
        return names.concat(layerName);
      if ( !isUse && names.indexOf(layerName) > -1 )
        return names.filter(name => name !== layerName);
      return names;
    });
  }, []);

  const [layerWmsTypes, setLayerWmsTypes] = useState([
    ...CONSTANTS_INITIAL_LAYER_WMS_TYPES,
    ...(INITIAL_LAYER_WMS_TYPES || []),
  ]);

  const [mountainReceiveSensitivityLayerList, setMountainReceiveSensitivityLayerList] = useState([]);
  const [sensitivityLayerListWithband, setSensitivityLayerListWithband] = useState([]);
  const [mountainExtraLayerDict, setMountainExtraLayerDict] = useState({});

  const [isClusteringPoint, setIsClusteringPoint] = useState(false);
  const [isHeapmapByPoint, setIsHeapmapByPoint] = useState(false);
  const [isNationalParkHeapmap, setIsNationalParkHeapmap] = useState(false);

  const iconDict = useIconDict();

  const [ chartDivVisible, setChartDivVisible ] = useState(false);
  const [ chartLoadingTrigger, setChartLoadingTrigger ] = useState('N');
  const [ chartAjaxParams, setChartAjaxParams ] = useState({});
  const [ tableAjaxParams, setTableAjaxParams ] = useState({});
  const [ districtLv1, setDistrictLv1] = useState({});
  const [ notAllAreaPolygon, setNotAllAreaPolygon ] = useState(false);

  const [fiveG_layer, setFiveG_layer] = useState('');

  return {
    viewState,
    dispatchViewState,
    geojsonExtraParams, setGeojsonExtraParams,
    geojsonPolygonLayerProps,
    geojsonPointLayerProps,
    pointLayerProps, setPointsLayerFilterFunc,
    dataResponseList, setDataResponseList,
    visibleRadius, setVisibleRadius,
    mapBounds, setMapBounds,
    setMapCenter, setZoom,
    isEnableDistanceToClickedPoint, setIsEnableDistanceToClickedPoint,
    clickedPointIconObjects,
    setClickedPointIconObjects,
    clickedPolygonGeojsonObjects,
    setClickedPolygonGeojsonObjects,
    widgetState, onChangeWidgetState,

    currentDistrictCode, setCurrentDistrictCode,
    manualDistrictLayerParamsByGeoserver, setManualDistrictLayerParamsByGeoserver,

    geojsonPolygonsResponseList, setGeojsonPolygonsResponseList,
    setPointsResponseList, pointsResponseList,
    filteredPointsResponseList,
    isHideFilteredPointsLayer, setIsHideFilteredPointsLayer,
    mapDistrictStats, setMapDistrictStats, isChangedMapDistrictStats,
    tooltipParams, setTooltipParams,
    mapViewType, setNullForMapViewType, setSearchByUidForMapViewType,
    mapType, setMapType,
    mapLayerNames, updateMapLayerNames,
    layerWmsTypes,
    mountainReceiveSensitivityLayerList,
    setMountainReceiveSensitivityLayerList,
    sensitivityLayerListWithband,
    setSensitivityLayerListWithband,
    mountainExtraLayerDict,
    setMountainExtraLayerDict,
    isClusteringPoint, setIsClusteringPoint,
    isHeapmapByPoint, setIsHeapmapByPoint,
    iconDict,
    chartDivVisible, setChartDivVisible,
    chartLoadingTrigger, setChartLoadingTrigger,
    chartAjaxParams, setChartAjaxParams,
    tableAjaxParams, setTableAjaxParams,
    districtLv1, setDistrictLv1,
    currentDistrictName, setCurrentDistrictName,
    notAllAreaPolygon, setNotAllAreaPolygon,
    setFilteredPointsResponseList,
    isNationalParkHeapmap, setIsNationalParkHeapmap,
    fiveG_layer, setFiveG_layer
  };
};


export function correctFeatureProperties(feature) {
  const {properties: {SID_CD, SIG_CD, pk, SID_KOR_NM, SIG_KOR_NM, name}} = feature;
  feature.properties.pk = pk || SID_CD || SIG_CD;
  feature.properties.name = name || SID_KOR_NM || SIG_KOR_NM;
  return feature;
}


export default useMapProps;
